import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { HiOutlineUpload, HiPhoneIncoming } from "react-icons/hi";
import { FaPlus } from "react-icons/fa6";
import {
  Button,
  DateInput,
  InputField,
  DropDownInput,
  DataTable,
  OptionGroup,
} from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getEscalationData, getEscalationTypeLabel } from "../../../utils/Data";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData, useApi } from "@/";
import { subDays } from "date-fns";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchEscalationData, selectAllEscalations } from "../../../app/Slices";
import { RightWrapper } from "../../../components";
import EscalationBulkForm from "./EscalationBulkForm";
import { RiFileExcel2Line } from "react-icons/ri";

const Escalations = () => {
  const escalationType = useMemo(
    () => [
      {
        label: "Pickup",
        value: "pickup",
      },
      {
        label: "Pre Delivery",
        value: "preDelivery",
      },
      {
        label: "Post Delivery",
        value: "postDelivery",
      },
      {
        label: "RTO",
        value: "rto",
      },
      {
        label: "Claim",
        value: "claims",
      },
      {
        label: "Finance",
        value: "finance",
      },
      {
        label: "Weight Dispute",
        value: "weightDispute",
      },
      {
        label: "KYC",
        value: "kyc",
      },
      {
        label: "Technical Support",
        value: "technicalSupport",
      },
      { label: "Other", value: "other" },
    ],
    [],
  );
  const statusType = useMemo(
    () => [
      { label: "Open", value: "Open" },
      { label: "In Progress", value: "In Progress" },
      { label: "Pending", value: "Pending" },
      { label: "Closed", value: "Closed" },
      { label: "Resolved", value: "Resolved" },
      { label: "Escalated", value: "Escalated" },
    ],
    [],
  );
  const dispatch = useDispatch();
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    date: {
      from: searchParams.get("from")
        ? new Date(searchParams.get("from"))
        : startOfMonthDate,
      to: searchParams.get("to")
        ? new Date(searchParams.get("to"))
        : endOfMonthDate,
    },
    customerName: searchParams.get("customerName") || "",
    customerId: searchParams.get("customerId") || "",
    escalationId: searchParams.get("escalationId") || "",
    shippingId: searchParams.get("shippingId") || "",
    awbs: searchParams.get("awbs") || "",
    escalationType: searchParams.get("escalationType") || "",
    status: searchParams.get("status") || "",
  }));

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [selectedRows, setSelectedRows] = useState([]);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const { callApi } = useApi();

  const fetchData = async () => {
    const response = await dispatch(
      fetchEscalationData({
        startDate: filters.date.from,
        endDate: filters.date.to,
      }),
    );
    console.log("response::::::", response);
  };
  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  // Ensure rawData is only generated once
  const rawData = useMemo(() => getEscalationData(100), []);
  const escalations = useSelector(selectAllEscalations) || [];

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "user.name" },
      customerId: { type: "partial", itemKey: "user.clientId" },
      awbs: { type: "comma", itemKey: "shipments.awbNumber" },
      shippingId: { type: "partial", itemKey: "shipments.shippingId" },
      escalationId: { type: "partial", itemKey: "escalationId" },
      status: { type: "partial", itemKey: "status" },
      escalationType: { type: "exact", itemKey: "escalationType" },
      date: { type: "date", itemKey: "escalationDate" },
    }),
    [],
  );
  const options = useMemo(
    () => [
      {
        label: "Export",
        value: "Export",
        icon: <RiFileExcel2Line />,
      },
    ],
    [],
  );
  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    escalations,
    appliedFilters,
    filterConfig,
  );
  const navigate = useNavigate();
  const handleView = (escalationId) => {
    console.log(escalationId);
  };
  // Memoized table columns
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "",
        cell: (info) => info.row.original.user?.name || "N/A",
        meta: {
          tooltip: (row) => row.user?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "",
        cell: (info) => info.row.original.user?.clientId || "N/A",
        meta: {
          tooltip: (row) => row.user?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Escalation ID",
        accessorKey: "escalationId",
        meta: {
          tooltip: (row) => row.escalationId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Escalation Date",
        accessorKey: "escalationDate",
        cell: (info) => formatDate(info.row.original.escalationDate),
        meta: {
          tooltip: (row) => formatDate(row.escalationDate),
        },
      }),
      ColumnHelper({
        header: "Escalation Type",
        accessorKey: "escalationType",
        cell: (info) =>
          getEscalationTypeLabel(info.row.original.escalationType) || "N/A",
        meta: {
          tooltip: (row) => getEscalationTypeLabel(row.escalationType) || "N/A",
        },
      }),
      ColumnHelper({
        header: "Shipping Id",
        accessorKey: "",
        cell: (info) =>
          info.row.original.shipments?.map((s) => s?.shippingId).join(",") ||
          "N/A",
        meta: {
          tooltip: (row) =>
            row.shipments?.map((s) => s?.shippingId).join(",") || "N/A",
        },
      }),
      ColumnHelper({
        header: "AWB ",
        accessorKey: "",
        cell: (info) =>
          info.row.original.shipments?.map((s) => s?.awbNumber).join(",") ||
          "N/A",
        meta: {
          tooltip: (row) =>
            row.shipments?.map((s) => s?.awbNumber).join(",") || "N/A",
        },
      }),
      ColumnHelper({
        header: "Escalation Status",
        accessorKey: "status",
        meta: {
          tooltip: (row) => row.status || "N/A",
        },
      }),
      ColumnHelper({
        header: "Action",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1  items-center">
            <Link to={`remarks/${info.row.original._id}`}>
              <Button
                size="xs"
                onClick={() => handleView(info.row.original._id)}
              >
                View
              </Button>
            </Link>
          </div>
        ),
        meta: {
          tooltip: () => "View escalation details",
        },
      }),
    ],
    [],
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  const handleExport = async (type) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = `Downloading ${type}...`;
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);

    try {
      console.log(`Downloading ${type}:`, selectedRows);

      const filename = `Escalation_${Date.now()}`;

      // Format the date range properly for the API
      const response = await callApi(
        "/common/export",
        "POST",
        {
          model: "Admin: Export Escalation",
          ids: selectedRows.map((r) => r._id),
          filename,
        },
        null,
        null,
        "blob",
      );

      const contentType = response?.headers?.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        console.error("Export failed:", errorData.message);
        return; // Exit early
      }

      // Proceed with blob handling if not JSON
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.csv`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);
    switch (value) {
      case "Export":
        handleExport("Export");
        break;
    }
  };

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);

    // Sync filters to URL search params
    const params = new URLSearchParams();
    if (filters.date?.from)
      params.set(
        "from",
        (filters.date.from instanceof Date
          ? filters.date.from
          : new Date(filters.date.from)
        ).toISOString(),
      );
    if (filters.date?.to)
      params.set(
        "to",
        (filters.date.to instanceof Date
          ? filters.date.to
          : new Date(filters.date.to)
        ).toISOString(),
      );
    if (filters.customerName) params.set("customerName", filters.customerName);
    if (filters.customerId) params.set("customerId", filters.customerId);
    if (filters.escalationId) params.set("escalationId", filters.escalationId);
    if (filters.shippingId) params.set("shippingId", filters.shippingId);
    if (filters.awbs) params.set("awbs", filters.awbs);
    if (filters.escalationType)
      params.set("escalationType", filters.escalationType);
    if (filters.status) params.set("status", filters.status);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      date: { from: startOfMonthDate, to: endOfMonthDate },
      customerName: "",
      customerId: "",
      escalationId: "",
      shippingId: "",
      awbs: "",
      escalationType: "",
      status: "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const handleBulk = () => {
    setSheetOpen(true); // Opens the sheet for bulk updates
  };
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent aria-describedby="">
            <SheetHeader>
              <SheetTitle className="font-nunito text-headline5 md:text-headline4 text-secondary/90 font-bold">
                Bulk Update Escalation
              </SheetTitle>
            </SheetHeader>
            <EscalationBulkForm setSheetOpen={setSheetOpen} />
          </SheetContent>
        </Sheet>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          {/* Date Filter */}
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />

          {/* Escalation ID Input */}
          <InputField
            label="Escalation ID"
            placeholder="Enter Escalation ID"
            name="escalationId"
            value={filters.escalationId}
            onChange={(value) => handleChange("escalationId", value)}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter customer name"
            name="customerName"
            value={filters.customerName}
            onChange={(value) => handleChange("customerName", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter customer ID"
            name="customerId"
            value={filters.customerId}
            onChange={(value) => handleChange("customerId", value)}
          />

          {/* Shipping ID Input */}
          <InputField
            label="Shipping ID"
            placeholder="Enter Shipping ID"
            name="shippingId"
            value={filters.shippingId}
            onChange={(value) => handleChange("shippingId", value)}
          />

          {/* Escalation Type Dropdown */}
          <DropDownInput
            label="Escalation Type"
            name="escalationType"
            placeholder="Select Escalation Type"
            options={escalationType}
            value={filters.escalationType}
            onChange={(value) => handleChange("escalationType", value)}
          />

          {/* Status Dropdown */}
          <DropDownInput
            label="EscalationsStatus"
            name="status"
            placeholder="Select Status"
            options={statusType}
            value={filters.status}
            onChange={(value) => handleChange("status", value)}
          />
          <InputField
            label="AWB"
            placeholder="Enter AWB"
            name="awbs"
            value={filters.awbs}
            onChange={(value) => handleChange("awbs", value)}
          />

          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-fit justify-end items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}
        <DataTable
          columns={columns}
          tittle="Escalations"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          isLoading={isLoading}
          resetPage={resetPageCounter}
          actions={[
            { name: "Bulk Update", onClick: handleBulk, icon: HiOutlineUpload },
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export Escalation",
                filename: `Escalations${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
    </RightWrapper>
  );
};

export default Escalations;
